<template>
  <div>
    <Card style="margin-bottom:16px">
      <Row class="main-banner">
        <div class="title-banner">
          <h3>{{ `${query.status === 'maintenance' ? '[Logic Under Maintenance] ' : ''}${query.name}` }}</h3>
          <div class="tags-row">
            <Tag
              v-for="tag in query.tags"
              :key="tag"
              :title="tag"
              class="tag"
              @click.native="toggleTag(tag, true)"
            >
              #{{ tag }}
            </Tag>
            <Tooltip
              content="Created Date"
              placement="top"
            >
              <span style="font-size: 12px;font-weight: normal;display: flex;padding-top: 5px;">
                ({{ `Created ${$moment(new Date(query.date)).format('YYYY-MM-DD')}` }})
              </span>
            </Tooltip>
          </div>
        </div>
        <div
          v-show="!isWizard"
          class="icons-banner"
        >
          <Tooltip content="Edit in Query Pad">
            <i
              class="far fa-edit action-icon"
              @click="onCopyToQueryPadIconClick(query)"
            />
          </Tooltip>
          <Tooltip content="Copy query to Clipboard">
            <i
              class="far fa-clone action-icon"
              @click="copyQuery(query)"
            />
          </Tooltip>
          <Tooltip :content="`Email ${query.author.email}`">
            <i
              class="far fa-envelope action-icon"
              @click="onSendFeedback(query.file, query.id, query.author.email, query.name)"
            />
          </Tooltip>
          <Tooltip content="Share Query">
            <i
              class="fas fa-share-alt action-icon"
              @click="onShareLink(query.file, query.id)"
            />
          </Tooltip>
          <Tooltip content="Favorite Query">
            <i
              :class="favoriteIcon(query.isFavorite)"
              @click="toggleFavorite(query.id, { isActive: !query.isFavorite, id: query.favoriteId })"
            />
          </Tooltip>
        </div>
      </Row>
      <div style="padding-bottom: 8px;">
        {{ query.description }}
      </div>
      <Collapse
        v-if="!isWizard"
        :value="query.collapseQuery"
        class="collapse"
        style="margin-bottom: 8px"
        @on-change="panelName => onToggleQuery(panelName, query)"
      >
        <Panel :name="query.name">
          <b>Query</b>
          <div
            v-if="query.collapseQuery"
            slot="content"
          >
            <Tabs
              v-model="query.show"
              :animated="false"
              type="card"
              class="tabs-container"
              @on-click="focusQuery(query)"
            >
              <TabPane
                name="template"
                label="Template"
              >
                <codemirror
                  :ref="`cm-template-${query.name}`"
                  :value="query.lines"
                  :options="cmOptions"
                  class="query-container"
                  @ready="onCmBlur"
                  @focus="onCmFocus"
                  @blur="onCmBlur"
                />
              </TabPane>
              <TabPane
                name="generated"
                label="Generated"
              >
                <codemirror
                  v-show="query.show === 'generated'"
                  :ref="`cm-generated-${query.name}`"
                  :value="query.queryStr || 'Fill out the mandatory parameters and click on &quot;Run Query&quot;!'"
                  :options="cmOptions"
                  class="query-container"
                  @ready="onCmBlur"
                  @focus="onCmFocus"
                  @blur="onCmBlur"
                />
              </TabPane>
            </Tabs>
          </div>
        </Panel>
      </Collapse>
      <div
        v-else
        style="margin-bottom: 16px"
      >
        <Tabs
          v-model="query.show"
          :animated="false"
          name="queryTemplate"
          type="card"
          class="tabs-container"
          @on-click="focusQuery(query)"
        >
          <TabPane
            name="template"
            label="Template"
            tab="queryTemplate"
          >
            <codemirror
              :ref="`cm-template-${query.name}`"
              :value="query.lines"
              :options="cmOptions"
              class="query-container"
              @ready="onCmBlur"
              @focus="onCmFocus"
              @blur="onCmBlur"
            />
          </TabPane>
          <TabPane
            name="generated"
            label="Generated"
            tab="queryTemplate"
          >
            <codemirror
              v-show="query.show === 'generated'"
              :ref="`cm-generated-${query.name}`"
              :value="query.queryStr || 'Fill out the mandatory parameters and click on &quot;Run Query&quot;!'"
              :options="cmOptions"
              class="query-container"
              @ready="onCmBlur"
              @focus="onCmFocus"
              @blur="onCmBlur"
            />
          </TabPane>
        </Tabs>
      </div>

      <Collapse
        v-if="showParams"
        :value="query.collapseValue"
        class="collapse"
        style="margin-bottom: 8px"
        @on-change="panelName => onCollapseChange(panelName, query)"
      >
        <Panel :name="query.name">
          <b>Inputs</b>
          <Form
            v-if="query.collapseValue"
            slot="content"
          >
            <FormItem
              v-for="(param, index) in query.params"
              :key="index"
              class="parameter"
            >
              <Row type="flex">
                <FormItem
                  style="display: flex"
                  class="param-form-item"
                >
                  <i
                    v-if="valueEnterRequired(param) && !isWizard"
                    slot="label"
                    style="color: var(--color-red); font-size: 65%; margin-right: 2px;"
                    class="fas fa-asterisk"
                  />
                  <Tooltip
                    slot="label"
                    :content="param.description"
                    placement="top-start"
                    max-width="200"
                  >
                    <b>{{ param.nameToDisplay }}</b>
                  </Tooltip>
                  <Select
                    v-if="param.options && param.options.length > 0"
                    v-model="param.value"
                    placeholder="Select Value"
                    clearable
                    @on-change="unsetQueryStr(query)"
                  >
                    <Option
                      v-for="option in param.options"
                      :key="option"
                      :label="option"
                      :value="option"
                    />
                  </Select>
                  <DatePicker
                    v-else-if="param.type === 'date'"
                    v-model="param.value"
                    placeholder="Select Date"
                    type="date"
                    format="yyyy-MM-dd"
                    clearable
                    @on-change="unsetQueryStr(query)"
                  />
                  <TimePicker
                    v-else-if="param.type === 'time'"
                    v-model="param.value"
                    placeholder="Select Time"
                    type="time"
                    format="HH:mm:ss"
                    clearable
                    @on-change="unsetQueryStr(query)"
                  />
                  <DatePicker
                    v-else-if="param.type === 'datetime'"
                    v-model="param.value"
                    placeholder="Select Date & Time"
                    type="datetime"
                    format="yyyy-MM-dd HH:mm:ss"
                    clearable
                    @on-change="unsetQueryStr(query)"
                  />
                  <InputNumber
                    v-else-if="param.type === 'number'"
                    v-model="param.value"
                    :min="0"
                    :placeholder="inputPlaceholder(param)"
                    clearable
                    @on-change="unsetQueryStr(query)"
                  />
                  <InputNumber
                    v-else-if="param.type === 'decimal'"
                    v-model="param.value"
                    :min="0"
                    :placeholder="inputPlaceholder(param)"
                    clearable
                    style="width: 150px"
                    @on-change="unsetQueryStr(query)"
                  />
                  <div
                    v-else-if="param.type === 'csv'"
                    style="display: flex; flex-direction: row;"
                  >
                    <Input
                      v-model="param.value"
                      :name="param.name"
                      :placeholder="inputPlaceholder(param)"
                      :readonly="true"
                      style="display: none;"
                      @on-change="unsetQueryStr(query)"
                    />
                    <Upload
                      :ref="`upload-${query.id}`"
                      name="upfile"
                      :with-credentials="true"
                      :action="uploadCsvUrl"
                      :format="['csv']"
                      :max-size="2048"
                      :before-upload="(file) => clearFilesBeforeUpload(query.id, file)"
                      :on-exceeded-size="handleMaxSize"
                      :on-format-error="(file) => onUploadFormatError(query.id, file)"
                      :on-success="(res, file, fileList) => onUploadSuccess(param, res, file, fileList)"
                      :on-error="onUploadError"
                    >
                      <Button
                        icon="ios-cloud-upload-outline"
                      >
                        Upload Files
                      </Button>
                    </Upload>
                  </div>
                  <ScrollSelect
                    v-else-if="param.input === 'many' && goldenQueryParams[param.name.toLowerCase()] && param.name.toLowerCase() === 'card_issuer'"
                    v-model="param.value"
                    :options="goldenQueryParams[param.name.toLowerCase()].map(value => ({ value, label: value }))"
                    multiple
                    @on-change="unsetQueryStr(query)"
                  />
                  <Select
                    v-else-if="param.input === 'many' && goldenQueryParams[param.name.toLowerCase()]"
                    v-model="param.value"
                    multiple
                    filterable
                    clearable
                    @on-change="unsetQueryStr(query)"
                  >
                    <Option
                      v-for="option in (Array.isArray(goldenQueryParams[param.name.toLowerCase()]) || !!!checkParentParamExistence(param))
                        ? goldenQueryParams[param.name.toLowerCase()]
                        : goldenQueryParams[param.name.toLowerCase()][checkParentParamExistence(param).value]"
                      :key="option"
                      :value="option"
                    >
                      {{ option }}
                    </Option>
                  </Select>
                  <AutoComplete
                    v-else-if="goldenQueryParams[param.name.toLowerCase()]"
                    v-model="param.value"
                    :data="(Array.isArray(goldenQueryParams[param.name.toLowerCase()]) || !!!checkParentParamExistence(param))
                      ? goldenQueryParams[param.name.toLowerCase()]
                      : goldenQueryParams[param.name.toLowerCase()][checkParentParamExistence(param).value]"
                    placeholder="Input value"
                    :filter-method="autoCompleteFilterMethod"
                    clearable
                    class="query-param-autocomplete"
                    @on-change="unsetQueryStr(query)"
                  />
                  <Input
                    v-else
                    v-model="param.value"
                    :placeholder="inputPlaceholder(param)"
                    clearable
                    @on-change="unsetQueryStr(query)"
                  />
                </FormItem>
              </Row>
            </FormItem>
          </Form>
        </Panel>
      </Collapse>
      <div style="display: flex;">
        <Button
          :disabled="!areParamsValid(query)"
          style="margin-right: 16px"
          @click="runQuery(query)"
        >
          Run Query
        </Button>
        <Button
          v-show="query.showResult"
          @click="toggleResult(query)"
        >
          Hide Result
        </Button>
        <Button
          v-show="query.runQuery && !query.showResult"
          @click="toggleResult(query)"
        >
          Show Result
        </Button>
      </div>
      <div v-show="query.showResult">
        <QueryTable
          v-if="query.queryStr"
          :query="query.runQuery || ''"
          :results="[]"
          :context="context"
          :style="{ backgroundColor: 'var(--color-white)', borderLeft: 'unset' }"
          white-background
          @outcome="(index, resultData) => updateResults(resultData)"
        />
      </div>
    </Card>
  </div>
</template>
<script>
import QueryTable from './QueryTable'
import ScrollSelect from '@/components/helper-components/ScrollSelect'
import _ from 'lodash'

const CASCADE_DATA_MAPPING = {
  mega_region: null,
  core_region: 'mega_region',
  region: 'core_region',
  subregion: 'region'
}

export default {

  components: {
    QueryTable,
    ScrollSelect
  },

  props: {
    query: {
      type: Object,
      default: () => {}
    },
    isWizard: {
      type: Boolean,
      default: false
    },
    goldenQueryParams: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      context: 'golden_query_library',
      tagsList: [],
      cmOptions: {
        tabSize: 4,
        mode: 'text/x-sql',
        lineNumbers: true,
        readOnly: true,
        autoRefresh: true
      },
      uploadCsvUrl: `${process.env.VUE_APP_API_URI}/golden-query/param-csv`,
      cascadeDataMapping: CASCADE_DATA_MAPPING
    }
  },

  computed: {
    showParams () {
      return this.query.params.length > 0
    }
  },

  watch: {
    'query.name' (newQueryName) {
      this.captitalizeParamName(newQueryName)
    },

    isWizard (isWizard) {
      this.query.show = !this.query.queryStr ? 'template' : 'generated'

      if (!isWizard) {
        this.query.show = 'generated'
        return
      }

      this.focusQuery(this.query)
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('goldenQuery')) {
      this.$set(this.$root.preferences, 'goldenQuery', {})
    }

    if (this.$root.preferences.hasOwnProperty('goldenQueryLibrary')) {
      this.$set(this.$root.preferences, 'goldenQuery', this.$root.preferences.goldenQueryLibrary)
      delete this.$root.preferences.goldenQueryLibrary
    }
  },

  created () {
    this.captitalizeParamName(this.query.name)

    if (this.isWizard) {
      this.query.show = 'template'
      this.query.collapseValue = this.query.name
      this.focusQuery()
    }
  },

  methods: {
    /**
     * Check if the child parameter has a valid parent parameter
     * To fetch data in a cascading manner
     * @param {Object} param the parameter object gettting proccessed
     * @return {Object|Undefined} the parent params of current param if exist
     */
    checkParentParamExistence (param) {
      return this.query.params.find(queryParam => queryParam.name.toLowerCase() === this.cascadeDataMapping[param.name.toLowerCase()])
    },

    /**
     * Make the parameter name capitalized
     * @param {String} newQueryName
     */
    captitalizeParamName (newQueryName) {
      this.query.params.map(param => {
        this.$set(param, 'nameToDisplay', _.startCase(_.lowerCase(param.name)))
      })

      this.$nextTick(() => {
        this.query.collapseValue = newQueryName
      })
    },

    /**
     * Set focus to codemirror: Template / Generated.
     * @param {Object} query
     */
    focusQuery (query = this.query) {
      this.$nextTick(() => {
        const cm = this.$refs[`cm-${query.show}-${query.name}`]
        const codeMirror = cm.length > 0 ? cm[0].codemirror : cm.codemirror
        // once we get our codemirror reference lets set the size of the generated query
        // so that it is more presentable.
        this.onCmFocus(codeMirror)
      })
    },

    /**
     * Generates a query according with the template parameters.
     * @param {Object} query
     */
    generateQuery (query) {
      let queryStr = query.lines
      const queryParams = {
        file: query.file || null,
        params: []
      }
      const logExtra = {
        options: {}
      }
      query.params.forEach(param => {
        const { name, value, type, optional = false, input = null } = param

        /**
         * If the current field is "optional" and it's empty (NULL, UNDEFINED or empty string, or empty array), then it will be removed that line from the Query.
         * NOTE: This validation checks also that the current value is not a number because if "value" is equal to "0", then it will be considered
         * as a "false" (!value). So with this validation we make sure that "value" is "null", "undefined" or an empty string, so we can remove
         * that line from the query.
         */
        if (optional && (_.isNil(value) || _.isEmpty(value)) && !_.isNumber(value)) {
          const querySplit = queryStr.split('\n')
          const indexToRemove = querySplit.findIndex(line => line.includes(`{{ ${name} }}`))
          if (indexToRemove >= 0) {
            querySplit.splice(indexToRemove, 1)
          }
          queryStr = querySplit.join('\n')
          return
        }

        let replaceValue = value
        logExtra.options[name] = value

        if (type === 'date') {
          replaceValue = this.$moment(new Date(replaceValue)).format('YYYY-MM-DD')
        } else if (type === 'datetime') {
          replaceValue = this.$moment(new Date(replaceValue)).format('YYYY-MM-DD HH:mm:ss')
        }
        if (input === 'many') {
          replaceValue = replaceValue.map(item => `'${item}'`).join()
        }

        queryStr = queryStr.replace(new RegExp('{{ *' + name + ' *}}', 'g'), replaceValue)

        const paramData = {
          name: param.name,
          value: param.value
        }
        queryParams.params.push(paramData)
      })

      this.$nextTick(() => {
        this.$set(query, 'show', 'generated')
        this.$set(query, 'queryStr', queryStr)
        this.$set(this.$root.preferences.goldenQuery, 'generatedQueryParams', queryParams)

        if (this.isWizard) {
          this.focusQuery(query)
        }
      })
    },

    /**
     * Removes the generated Query
     * @param {Object} query
     */
    unsetQueryStr (query) {
      this.$set(query, 'show', 'template')
      this.$set(query, 'queryStr', '')
      this.$set(query, 'showResult', false)
      this.$set(query, 'runQuery', '')

      if (this.isWizard) {
        this.focusQuery(query)
      }
    },

    /**
     * Copies the generated query into the clipboard.
     * @param {Object} query
     */
    copyQuery (query) {
      this.$emit('copy-query', query)
    },

    /**
     * Function triggered by Upload's prop: on-success
     * Assign the parsed value of the CSV file returned by server directly to the query's param, which is the model
     * @param {Object} param the param attribute of a specific golden query
     * @param {Object} res
     * @param {Object} file
     * @param {Object} fileList
     */
    onUploadSuccess (param, res, file) {
      this.$log('csv_upload', file.name, { size: file.size, status: 'succeed' })
      param.value = JSON.stringify(res.content)
    },

    /**
     * Display an error message when the file format is not matched as settings
     * @param {Object} file
     */
    onUploadFormatError (queryId) {
      this.$Message.warning({
        content: `File type not supported. Please choose file types of ${this.$refs[`upload-${queryId}`][0].format.join(' or')}.`,
        duration: 5
      })
    },

    /**
     * Display an error message when the upload process fails
     * @param {Object} res
     * @param {Object} file
     * @param {Object} fileList
     */
    onUploadError () {
      this.$Message.warning({
        content: 'File upload failed. Please try again.',
        duration: 5
      })
    },

    /**
     * Clear the uploaded file before uploading a new one
     * Now only support uploading single file
     * @param {Number} queryId the query id passed down
     * @param {Object} file
     */
    clearFilesBeforeUpload (queryId) {
      this.$refs[`upload-${queryId}`][0].clearFiles()
    },

    /**
     * Display an error message when the file size exceeds 2M
     * @param {Object} file
     * @param {Object} fileList
     */
    handleMaxSize (file) {
      this.$log('csv_upload', file.name, { 'size': file.size, 'status:': 'maxSize exceeded' })
      this.$Notice.warning({
        title: 'Exceeding file size limit',
        desc: `File  ${file.name} is too large, no more than 2M allowed.`
      })
    },

    /**
     * Validates if the Parameters of a query are valids
     * @param {Object} query
     * @return {Boolean}
     */
    areParamsValid (query) {
      const validParams = query.params.every(param => {
        const isEmpty = _.isNil(param.value) || param.value.length === 0
        return param.optional || !isEmpty
      })
      if (validParams && !query.queryStr) {
        this.generateQuery(query)
      }

      return validParams
    },

    /**
     * Filter the results by the selected tag.
     * @param {String} tagName
     * @param {Boolean} isSelected
     */
    toggleTag (tagName, isSelected) {
      this.$emit('tag-selected', tagName, isSelected)
    },

    /**
     * Specifies the placeholder for an input object.
     * @param {Object} param
     * @return {String}
     */
    inputPlaceholder (param) {
      let placeholder = ''

      if (!param.type) {
        return placeholder
      }

      switch (param.type.toLowerCase()) {
        case 'number':
          placeholder = 'Enter a number'
          break
        default:
          placeholder = 'Enter text'
      }

      return placeholder
    },

    /**
     * Initializes a codemirror on ready and on blur.
     * @param {Object} cm
     */
    onCmBlur (cm) {
      cm.off('copy', () => {})
      cm.setSize(null, 100)
    },

    /**
     * Specifies the actions to do if user focuses a codemirror.
     * @param {Object} cm
     */
    onCmFocus (cm) {
      cm.on('copy', (cm, e) => {
        this.$log('query_copy', e.srcElement.value, { request: 'Shortcut copy from Golden Query.' })
      })
      cm.setSize(null, 300)
    },

    /**
     * Runs a generated query.
     * @param {Object} query
     */
    runQuery (query) {
      if (!this.isWizard) {
        this.$log('query_run', query.name, { query: query.queryStr })
      }
      this.$set(query, 'runQuery', query.queryStr)
      this.$set(query, 'showResult', true)
      this.$set(query, 'collapseValue', null)
    },

    /**
     * Hides/Show the query results.
     * @param {Object} query
     */
    toggleResult (query) {
      this.$set(query, 'showResult', !query.showResult)
    },

    /**
     * Shows the results from a query.
     * @param {Object} query
     */
    showResult (query) {
      this.$set(query, 'showResult', true)
    },

    /**
     * Copies the share link into the clipboard.
     * @param {String} fileName
     * @param {Number} queryId
     */
    onShareLink (fileName, queryId) {
      this.$emit('query-share', fileName, queryId)
    },

    onSendFeedback (fileName, queryId, queryOwnerEmail, queryName) {
      this.$emit('feedback', fileName, queryId, queryOwnerEmail, queryName)
    },

    /**
     * Copies the generated query into a new tab in Query Pad.
     * @param {Object} query
     */
    onCopyToQueryPadIconClick (query) {
      this.$emit('copy-to-query-pad', query)
    },

    /**
     * Auto completes a component.
     * @param {String} value
     * @param {String} option
     * @return {String}
     */
    autoCompleteFilterMethod (value, option) {
      return option.toUpperCase().indexOf(value.toUpperCase()) !== -1
    },

    /**
     * Toggles between expand or collapse behavior on a query's "Parameters" panel.
     * @param {String} panelName
     * @param {Object} query
     */
    onCollapseChange (panelName, query) {
      this.$nextTick(() => {
        this.$set(query, 'collapseValue', panelName)
      })
    },

    /**
     * Toggles between expand or collapse behavior for query body.
     * @param {String} panelName
     * @param {Object} query
     */
    onToggleQuery (panelName, query) {
      this.$nextTick(() => {
        this.$set(query, 'collapseQuery', panelName)
        this.focusQuery(query)
      })
    },

    /**
     * Updates the user preference such as "LIKES" or "FAVORITES" on an specific dashboard
     * @param {Number} queryId
     * @param {Object} properties Contains the properties regarding the "favorite" component (isActive, id).
     */
    toggleFavorite (queryId, properties) {
      this.$emit('fav-selected', queryId, properties)
    },

    /**
     * Defines the favorite icon to be displayed:
     *   - If selected, filled.
     *   - If unselected, outline.
     * @param {Boolean} isFavorite
     * @return {String}
     */
    favoriteIcon (isFavorite) {
      return isFavorite ? 'fas fa-heart action-icon' : 'far fa-heart action-icon'
    },

    /**
     * Updated results from Query Table
     * @param {Number} index Tab index to update
     * @param {Array} resultData Results for run queries
     * @param {Number} statementId The statement id of the query ran. Used by Snowflake for caching results
     */
    updateResults (resultData) {
      if (resultData.length && _.has(resultData[0], 'error')) {
        this.$emit('has-results', false)
      } else {
        this.$emit('has-results', true)
      }
    },

    valueEnterRequired (param) {
      return !param.optional && !param.value
    }
  }
}
</script>

<style scoped>
  .main-banner {
    margin-bottom: 8px;
    width: 100%;
    display: flex;
    flex-direction: row;
  }

  .title-banner {
    width: 100%;
    display: flex;
    flex-direction: row;
  }

  .title-banner .tags-row {
    margin-left: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .icons-banner {
    margin-right: 24px;
    width: 148px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .icons-banner div {
    width: 18px;
    text-align: center;
  }

  .icons-banner div:not(:last-child) {
    margin-right: 8px;
  }

  .col-left {
    width: calc(100% - 344px);
  }
  .col-right {
    width: 336px;
    margin-left: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .param-input {
    margin-bottom: 16px;
  }

  .generate-query-button-container {
    display: flex;
    justify-content: flex-end;
  }

  .query-container {
    height: 100%;
    width: 100%;
    border: 1px solid var(--color-gray-border);
    border-top: none;
  }

  .query-param-autocomplete >>> .ivu-auto-complete.ivu-select-dropdown {
    max-height: 250px;
  }

  .param-form-item {
    width: 80%;
  }

  .param-form-item >>> .ivu-form-item-label {
    width: 30%;
    text-align: left;
    font-size: 12px;
    display: flex;
    justify-content: flex-end;
  }

  .param-form-item >>> .ivu-form-item-content {
    width: 70%;
  }

  .no-queries-msg {
    text-align: center;
    font-size: 32px;
    margin-top: 15%;
    margin-bottom: 15%;
  }
  .tag {
    max-width: unset;
    border-color: var(--color-card-border);
    background-color: transparent;
    cursor: pointer;
  }
  .tag >>> .ivu-tag-text {
    color: var(--color-black);
  }

  .search-box,
  .search-box >>> .ivu-input {
    height: 40px;
  }

  .email-icon {
    margin: 0;
    margin-right: 8px;
    box-sizing: border-box;
    display: flex;
    align-items: baseline;
    justify-content: center;
    color: var(--color-green-lime);
  }

  .email-icon i {
    font-size: 20px;
    cursor: pointer;
  }

  .parameter {
    margin-bottom: 16px;
  }

  .parameter:last-child {
    margin-bottom: 0;
  }

  .parameter >>> .ivu-form-item-content {
    line-height: 0;
  }

  .parameter >>> .ivu-form-item-content .ivu-tooltip {
    width: 98%;
  }

  .action-icon {
    cursor: pointer;
    font-size: 16px;
  }

  .filter .dropdown .elements .row {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .filter .dropdown .elements .row .element-row,
  .filter .dropdown .elements .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .filter .dropdown .elements .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .filter .dropdown .elements .row .element-row p {
    margin: 0;
  }

  .right {
    display: flex;
    justify-content: flex-end;
  }

  .filter .dropdown .elements .row .select-input {
    width: 100%;
  }

  .filter .dropdown .elements .row .select-input.left >>> .ivu-select-selection {
    width: 100%;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.right >>> .ivu-select-selection {
    width: 100%;
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.big-list >>> .ivu-select-dropdown {
    transform-origin: center top !important;
    top: unset !important;
    max-height: 400px;
    overflow-y: scroll;
  }

  .filter .dropdown .elements .row .select-input >>> .ivu-select-not-found {
    color: var(--color-red-alert);
    font-size: 12px;
    list-style: none;
    text-align: left;
    padding: 7px 16px;
  }

  .tabs-container >>> .ivu-tabs-bar {
    position: relative;
    top: 0px;
    margin-bottom: 0;
    background-color: #fff;
  }

  .collapse {
    margin-bottom: 0;
  }

  .collapse >>> .ivu-collapse-content {
    overflow: unset !important;
  }
</style>
